import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Select, Input, Hidden } from '~/components/Form';
import {
  MOTORISTA_ABASTECIMENTO,
  MODULO_CTF,
  CUSTOMIZACAO_RAIZEN,
} from '~/constants/modules';
import { t, validCharacters, can } from '~/helpers';
import {
  storeEmpresasVeiculo,
  updateEmpresasVeiculo,
} from '~/store/modules/empresaveiculo/actions';

export default function CreateOrUpdate({
  reference,
  data,
  create,
  toggleConfirmButton,
}) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector(
    (state) => state.empresaveiculo
  );

  const initials = create ? data : { ...find, ...data };

  async function handleSubmit(param) {
    try {
      const schema = Yup.object().shape({
        idempresaveiculo: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(11, t('s.geral.validacao.tamanhomaximo').replace('%s', 11))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        nomeempresaveiculo: !can(CUSTOMIZACAO_RAIZEN)
          ? Yup.string()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .max(50, t('s.geral.validacao.tamanhomaximo').replace('%s', 50))
              .test(
                'characters-invalid',
                t('s.configuracao.geral.naopodecontercaracterespecial'),
                (value) => validCharacters(value)
              )
          : Yup.string().nullable(),

        exibemotoristaempresaveiculo: can(MOTORISTA_ABASTECIMENTO)
          ? Yup.number().required(t('s.geral.validacao.campoobrigatorio'))
          : Yup.string().nullable(),
        statusempresaveiculo: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(1, t('s.geral.validacao.tamanhomaximo').replace('%s', 1)),

        controlacotaempresaveiculo: can(MODULO_CTF)
          ? Yup.number().required(t('s.geral.validacao.campoobrigatorio'))
          : Yup.string().nullable(),

        // controlacotaempresaveiculo: Yup.number()
        //   .max(2, t('s.geral.validacao.tamanhomaximo').replace('%s', 2)),
      });

      await schema.validate(param, {
        abortEarly: false,
      });

      const objSend = {
        controlacotaempresaveiculo: can(MODULO_CTF)
          ? param.controlacotaempresaveiculo
          : 0,
        exibemotoristaempresaveiculo: 0,
        ...initials,
        ...param,
        idcliente: client,
      };

      console.log(objSend);

      if (create) {
        await dispatch(storeEmpresasVeiculo(objSend, params));
      } else {
        await dispatch(updateEmpresasVeiculo(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (!loading) {
      reference.current.setData(initials);

      console.log('Initials empresa veículo: ', initials);
    }
  }, [loading, initials]);

  useEffect(() => {
    toggleConfirmButton(true);
  }, []);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <Input
            name="idempresaveiculo"
            label={t('s.cadastro.empresaveiculo.idempresaveiculo')}
            variant="outlined"
            fullWidth
            size="small"
            disabled={!create}
            InputLabelProps={{
              shrink: true,
            }}
          />

          {!can(CUSTOMIZACAO_RAIZEN) ? (
            <Input
              name="nomeempresaveiculo"
              label={t('s.cadastro.empresaveiculo.nomeempresaveiculo')}
              variant="outlined"
              fullWidth
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
              disabled={can(CUSTOMIZACAO_RAIZEN)}
            />
          ) : (
            <Hidden name="nomeempresaveiculo" />
          )}

          {can(MOTORISTA_ABASTECIMENTO) && (
            <Select
              name="exibemotoristaempresaveiculo"
              label={t(
                's.cadastro.empresaveiculo.exibemotoristaempresaveiculo'
              )}
              options={[
                {
                  value: 0,
                  label: 'Não solicita motorista',
                },
                {
                  value: 1,
                  label: 'Solicita motorista',
                },
                // {
                //   value: 2,
                //   label: 'Solicita motorista e senha',
                // },
              ]}
            />
          )}

          {can(MODULO_CTF) ? (
            <Select
              name="controlacotaempresaveiculo"
              label={t('Controla Cota')}
              options={[
                {
                  value: 0,
                  label: 'Não controla',
                },
                {
                  value: 1,
                  label: 'Controla, mas pode abastecer offline',
                },
                {
                  value: 2,
                  label: 'Controla e não pode abastecer offline',
                },
              ]}
            />
          ) : (
            <Hidden name="controlacotaempresaveiculo" value={0} />
          )}

          <Select
            name="statusempresaveiculo"
            variant="outlined"
            label={t('s.cadastro.empresaveiculo.statusempresaveiculo')}
            options={[
              { value: 'L', label: t('s.geral.opcoes.liberado') },
              { value: 'B', label: t('s.geral.opcoes.bloqueado') },
              { value: 'C', label: t('s.geral.opcoes.cancelado') },
            ]}
            disabled={can(CUSTOMIZACAO_RAIZEN)}
          />
        </Form>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
  toggleConfirmButton: PropTypes.func.isRequired,
};

CreateOrUpdate.defaultProps = {
  data: {},
};
